import { Brand, LocaleSpecificGroup } from '../override';

/**
 * Define translatable keys
 *
 * Every key has to be globally unique!
 *
 * This uses object spread notation because that's the only way to join an
 * arbitrary number of objects in Typescript. Unfortunately that means this
 * method can't warn on key collisions.
 */
export function staticText<T>(
  group: LocaleSpecificGroup<T>,
): LocaleSpecificGroup<T> {
  // Sort keys for reproducible queries
  return Object.fromEntries(
    Object.entries(group).sort((a, b) => a[0].localeCompare(b[0])),
  ) as LocaleSpecificGroup<T>;
}

export const BRAND_SPECIFIC_FLAG_KEY = '__brand_specific__' as const;
export const BRAND_SPECIFIC_FLAG_VALUE = true as const;

export type BrandSpecificFlag = {
  [Key in typeof BRAND_SPECIFIC_FLAG_KEY]: typeof BRAND_SPECIFIC_FLAG_VALUE;
};

export type BrandSpecificN<T> = Partial<Brand<Partial<T>>> & { _: T };
export type BrandSpecificWithFlag<T> = BrandSpecificN<T> & BrandSpecificFlag;

export type Translated<T> =
  T extends LocaleSpecificGroup<infer S>
    ? S
    : T extends BrandSpecificWithFlag<LocaleSpecificGroup<infer S>>
      ? S
      : unknown;

export const brandSpecificStaticText = <T>(
  group: BrandSpecificN<T>,
): BrandSpecificWithFlag<T> => {
  // Sort keys for reproducible queries
  const sortedGroup = Object.fromEntries(
    Object.entries(group).sort((a, b) => a[0].localeCompare(b[0])),
  ) as BrandSpecificN<T>;

  return {
    ...sortedGroup,
    [BRAND_SPECIFIC_FLAG_KEY]: BRAND_SPECIFIC_FLAG_VALUE,
  };
};

export type TranslationWrapper<T> = Partial<
  Brand<Partial<LocaleSpecificGroup<T>>>
>;
export type TranslationUnWrapper<T> =
  T extends TranslationWrapper<infer U> ? U : never;
